import * as React from "react";
import type { AppUser } from "../../../services/userService";
import type { VorgangDto } from "../../../services/vorgangService";
import { artLangform, betreffOhneArt } from "../../../data/vorgangArt";
import { artOptions } from "../detail/overviewHead";
import { slotsFromDto, type CascadeResult } from "../detail/linkSlots";
import type { BeteiligtenFeld } from "../detail/personenZeile";
import { ZuweisungFeld } from "../v3/ZuweisungFeld";
import { AnsprechpartnerKnopf } from "../v3/AnsprechpartnerKnopf";
import { BeteiligteFeld } from "../v3/BeteiligteFeld";
import { BezugsKreise } from "../v3/BezugsKreise";
import { IcoCheck, IcoMehrWaagerecht } from "../v3/vg3Icons";
import { RdPopover } from "../../redesign/table/RdPopover";
import type { AustauschDto } from "../../../services/vorgangAustauschService";
import { AustauschKnopfV4 } from "./AustauschKnopfV4";
import { DatumChipV4 } from "./DatumChipV4";
import { kopfMeta } from "./kopfMeta";
import { VorgangUntertitelV4 } from "./VorgangUntertitelV4";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";
import { twvPatch } from "../shared/fristZurTwv";

/**
 * Der Vorgangskopf in V4 — er sieht in zwei Zuständen verschieden aus.
 *
 * ══ OHNE To-dos ════════════════════════════════════════════════════════════
 *     [Art] (Firma) Kontaktname                        Ansprechpartner (4)
 *     Vorgangsbetreff                 (K)(R)(V) (A) ⋯ [als erledigt markieren]
 *     TWV [Fr., 18.09.2026]  FRIST [—]     (○)      (○○)      (○)
 *                                        Hauptver.  Bearbeiter  zu informieren
 *     ────────────────────
 *     4711-2 · angelegt Fr., 05.09. von Mohamed Jre · zuletzt bearbeitet …
 *     Untertitel …
 *
 * ══ MIT To-dos — KONTAKTZEILE, ÜBERSCHRIFT, UNTERTITEL ═════════════════════
 *     [Art] (Firma) Kontaktname                        Ansprechpartner (4)
 *     Vorgangsbetreff
 *     Untertitel …
 *
 * Fristen, Rollen und der Erledigt-Knopf tragen die To-dos (PO 13.09.2026);
 * Betreff und Untertitel stehen seit dem 18.09.2026 wieder HIER: „Vorgang mit
 * To-dos hat Überschrift und Untertitel — erstes To-do hat dann dieselbe
 * Überschrift und den Verlauf des bisherigen Vorgangs" (Zettel vom
 * 18.09.2026).
 *
 * Damit steht der Betreff BEWUSST zweimal auf der Seite: einmal als Name des
 * Vorgangs, einmal als Name des ersten To-dos, das aus ihm entstanden ist
 * (`uebernimmVorgangsstrang`). Das ist keine Dopplung aus Versehen — die
 * Überschrift sagt, worum der ganze Vorgang geht, die To-do-Karte darunter,
 * woran gerade gearbeitet wird. Vom 13. bis 18.09.2026 galt die umgekehrte
 * Regel (`betreffTraeger`, entfernt); wer die alte Begründung sucht, findet
 * sie in der Geschichte von `vorgangAlsStrang.ts`.
 *
 * Die META-ZEILE (Nummer, Anlage, letzte Bearbeitung) entfällt mit To-dos
 * weiterhin: Sie ist Beiwerk, und der Kopf soll die Karten darunter nicht
 * zuwachsen. Ohne To-dos bleibt sie, `kopfMeta()` also auch.
 *
 * Zwei Unterschiede zu V3 sind fachlich, nicht bloß optisch:
 *
 *  · Die Rollen stehen EINZELN. V3 fasste Hauptverantwortliche und Bearbeiter
 *    zu „Beteiligte“ zusammen; wer die Akte übernimmt, muss aber wissen, wer
 *    führt und wer mitarbeitet — das ist nicht dasselbe.
 *  · Die Vorgangsart steht in der KONTAKTZEILE, vor dem Kunden (PO 13.09.2026).
 *    Sie gehört zum Vorgang und muss auch mit To-dos änderbar bleiben — in der
 *    Betreffzeile, wo sie bis dahin stand, wäre sie mit ihr verschwunden. In
 *    beiden Zuständen steht sie damit an derselben Stelle, und nur dort.
 *  · Der BETREFF steht ÜBER den Fristen (PO 13.09.2026): „Ich fang doch damit
 *    an, mit dem Betreff, worum es geht, bevor ich sehe, wann ist denn die
 *    Wiedervorlage dazu.“
 */
type Props = {
    vorgang: VorgangDto;
    titel: string;
    onTitel: (v: string) => void;
    beschreibung: string;
    onBeschreibung: (v: string) => void;
    users: AppUser[];
    canEdit: boolean;
    hatTodos: boolean;
    /** Aufgelöster Name des Erstellers; null = unbekannt. */
    ersteller: string | null;
    /** Jüngste Aktivität im Vorgang — letzte Notiz oder letztes Ereignis. */
    zuletzt: Date | null;
    savedFlash: boolean;
    saveError: string | null;
    onPatch: (data: Record<string, unknown>) => void;
    onPatchBeteiligte: (feld: BeteiligtenFeld, next: string[]) => void;
    onAnwendenLinks: (result: CascadeResult) => void;
    /**
     * Die Austausche des Vorgangs (T-302) — das A in der Betreffzeile zeigt
     * den des Vorgangs selbst (`todoId` null). Es steht nur OHNE To-dos da;
     * mit To-dos trägt jedes To-do sein eigenes.
     */
    austausch: readonly AustauschDto[];
    onAustauschGeaendert: () => void | Promise<void>;
    /**
     * Das ⋯-Menü neben dem A (PO 21.09.2026). Beide Einträge sind optional:
     * Fehlt der Handler, entfällt der Punkt — und sind beide fort, erscheint
     * das Menü gar nicht.
     *
     * Es steht nur in der Reihe OHNE To-dos, weil die ganze Reihe nur dort
     * steht. Genau dort gehört es auch hin: Ein Vorgang ohne To-dos ist der
     * Fall, in dem der Vorgang SELBST die Arbeit ist — und damit der einzige,
     * in dem „als To-do verschieben" überhaupt einen Sinn ergibt.
     */
    onTodoVerschieben?: () => void;
    onVorgangVerschieben?: () => void;
    /**
     * Seit dem 28.09.2026 zwei Einträge, die AUCH mit To-dos gelten (PO:
     * „Zusammenführen?" und „aus einem Vorgang zwei Vorgänge machen"). Mit
     * To-dos steht die Reihe rechts nicht — das ⋯ erscheint dann allein am
     * Ende der Betreffzeile, ohne Bezüge, A und Erledigt-Knopf.
     */
    onZusammenfuehren?: () => void;
    onTeilen?: () => void;
    /**
     * HANDY-LAYOUT (Plan „Vorgangsdetail mobil", PR 2; nach Breite, also auch
     * ein schmales PC-Fenster). Kompakt: Art, Betreff, Kontaktzeile mit
     * Anrufen/Mail — Fristen, Rollen, Bezüge und die Meta-Zeile liegen hinter
     * „Details". Ohne `mobil` bleibt der Kopf, wie er ist.
     */
    mobil?: boolean;
    /** Die runden Knöpfe der Kontaktzeile (Anrufen, Mail) — nur mit `mobil`. */
    kontaktKnoepfe?: React.ReactNode;
};

/**
 * Der Winkel an der Art-Pille. Ein eigener kleiner Pfeil statt `IcoChevron` aus
 * `vg3Icons`: der zeigt nach rechts bzw. links und hängt an V3 bis V6 — ihn zu
 * drehen drehte jeden Aufklapper der App. Hier braucht es genau einen nach
 * unten, und er soll nirgendwo sonst wirken.
 */
const ArtPfeil: React.FC = () => (
    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" aria-hidden focusable="false">
        <path
            d="M1 1l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Die Vorgangsart als Pille — AUSGESCHRIEBEN, wo ein Wort hinterlegt ist
 * („JG" → „Jahresgespräch", PO 13.09.2026), und nur so breit wie dieses Wort.
 *
 * Warum die Auswahl UNSICHTBAR darüberliegt statt als schlichtes `<select>`:
 * ein natives Auswahlfeld ist immer so breit wie seine LÄNGSTE Option. Mit
 * „Jahresgespräch" in der Liste stünde auch an einem NKG-Vorgang eine Pille in
 * dieser Breite — das Kürzel verloren in der Mitte. Der PO will das
 * ausdrücklich nicht („bei uns ist das ziemlich kurz"): keine Mindestbreite,
 * links und rechts dasselbe Polster. Also trägt ein sichtbares Wort die Breite,
 * und das Auswahlfeld liegt mit `opacity:0` darüber — es behält Tastatur,
 * Beschriftung und das native Menü des Browsers.
 *
 * OHNE gesetzte Art zeigt die Pille die erste Option, genau wie das native Feld
 * es bisher tat: dessen Wert passte dann auf keine Option, und der Browser
 * stellte die erste dar. Bestandsverhalten, hier nur sichtbar nachgebaut.
 */
const ArtPille: React.FC<{
    art: string | null | undefined;
    canEdit: boolean;
    onWahl: (art: string) => void;
}> = ({ art, canEdit, onWahl }) => {
    const optionen = artOptions(art);
    const gesetzt = (art ?? "").trim();
    const wort = artLangform(gesetzt) || artLangform(optionen[0] ?? "");
    return (
        <span className="vg4-art">
            <span className="vg4-art-wort">{wort}</span>
            {canEdit && (
                <span className="vg4-art-pfeil" aria-hidden>
                    <ArtPfeil />
                </span>
            )}
            <select
                className="vg4-art-wahl"
                value={gesetzt}
                disabled={!canEdit}
                aria-label="Vorgangsart"
                onChange={(e) => onWahl(e.target.value)}
            >
                {optionen.map((a) => (
                    <option key={a} value={a}>
                        {artLangform(a)}
                    </option>
                ))}
            </select>
        </span>
    );
};

/** Eine Rolle als Kreis mit kleiner Legende darunter. */
const Rolle: React.FC<{
    gruppe: BeteiligtenFeld;
    label: string;
    vorgang: VorgangDto;
    users: AppUser[];
    canEdit: boolean;
    onChange: (feld: BeteiligtenFeld, next: string[]) => void;
}> = ({ gruppe, label, vorgang, users, canEdit, onChange }) => (
    <BeteiligteFeld
        users={users}
        canEdit={canEdit}
        gruppen={[gruppe]}
        persoenlich={vorgang.persoenlich === true}
        werte={{
            hauptverantwortlich: vorgang.hauptverantwortlich ?? [],
            bearbeiter: (vorgang.bearbeiter ?? []).map(String),
            zuInformierende: vorgang.zuInformierende ?? [],
        }}
        onChange={(g, next) => onChange(g, next)}
        popoverTitel={label}
        leerLabel={label}
        mitLegende
        sucheBeiLeerOffen
        // 29 px wie die K·R·V-Kreise in der Zeile darüber (Mockup `.kopf`).
        kreisGroesse={29}
    />
);


/**
 * Schildtext zum Kunden-Chip: der primäre Kontaktstatus (T-332). Ohne Status
 * oder bei „Unbekannt" kein Schild — ein falsches „Kunde" ist schlechter als
 * keines.
 */
export function kontaktSchild(status: string | null | undefined): string | false {
    const s = String(status ?? "").trim();
    return s && s.toLowerCase() !== "unbekannt" ? s : false;
}

export const VorgangKopfV4: React.FC<Props> = ({
    vorgang,
    titel,
    onTitel,
    beschreibung,
    onBeschreibung,
    users,
    canEdit,
    hatTodos,
    ersteller,
    zuletzt,
    savedFlash,
    saveError,
    onPatch,
    onPatchBeteiligte,
    onAnwendenLinks,
    austausch,
    onAustauschGeaendert,
    onTodoVerschieben,
    onVorgangVerschieben,
    onZusammenfuehren,
    onTeilen,
    mobil = false,
    kontaktKnoepfe,
}) => {
    /** Handy: „Details" ist standardmäßig zu (Artifact „Vorgangsdetail mobil"). */
    const [details, setDetails] = React.useState(false);
    const erledigt = (vorgang.status ?? "Offen") === "Erledigt";
    /* Ohne Bearbeitungsrecht kein ⋯ — beide Einträge schreiben. Dieselbe
       Bedingung wie am To-do (`canEdit && canRemoveTodo(canEdit)`). */
    // Die beiden Verschiebe-Wege gelten nur OHNE To-dos (PO 21.09.2026) —
    // auch wenn ein Handler gesetzt ist. Zusammenführen und Teilen immer.
    const zeigeTodoVerschieben = !hatTodos && onTodoVerschieben != null;
    const zeigeVorgangVerschieben = !hatTodos && onVorgangVerschieben != null;
    const zeigeMehr =
        canEdit &&
        (zeigeTodoVerschieben || zeigeVorgangVerschieben || onZusammenfuehren != null || onTeilen != null);
    // Ein PERSÖNLICHER Vorgang trägt keine Bezüge: das Backend lehnt Kunde,
    // Risiko und Vertrag mit 400 ab („Ein persönlicher Vorgang trägt keine
    // Bezüge.“, VorgangService.update). Deshalb steht hier weder ein Kreis
    // noch ein Zuweisen-Eintrag — `ZuweisungFeld` sagt an derselben Stelle
    // ohnehin, dass es keine gibt.
    const persoenlich = vorgang.persoenlich === true;

    /** Das ⋯ — mit und ohne To-dos dasselbe Menü (PO 21. und 28.09.2026). */
    const mehrMenue = zeigeMehr ? (
        <RdPopover
            width={260}
            dialogLabel="Weitere Vorgangs-Aktionen"
            align="end"
            trigger={({ toggle: auf, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    className="vg3-ibtn"
                    title="Weitere Vorgangs-Aktionen"
                    aria-label="Weitere Vorgangs-Aktionen"
                    onClick={auf}
                >
                    <IcoMehrWaagerecht size={16} />
                </button>
            )}
        >
            {(close) => (
                <div className="kvp-rd vg3-menu">
                    {zeigeTodoVerschieben && onTodoVerschieben && (
                        <button
                            type="button"
                            className="vg3-menu-item"
                            onClick={() => {
                                close();
                                onTodoVerschieben();
                            }}
                        >
                            To-do hierher verschieben…
                        </button>
                    )}
                    {zeigeVorgangVerschieben && onVorgangVerschieben && (
                        <button
                            type="button"
                            className="vg3-menu-item"
                            onClick={() => {
                                close();
                                onVorgangVerschieben();
                            }}
                        >
                            Diesen Vorgang als To-do verschieben…
                        </button>
                    )}
                    {onZusammenfuehren && (
                        <button
                            type="button"
                            className="vg3-menu-item"
                            onClick={() => {
                                close();
                                onZusammenfuehren();
                            }}
                        >
                            Mit anderem Vorgang zusammenführen…
                        </button>
                    )}
                    {onTeilen && (
                        <button
                            type="button"
                            className="vg3-menu-item"
                            onClick={() => {
                                close();
                                onTeilen();
                            }}
                        >
                            Vorgang teilen…
                        </button>
                    )}
                </div>
            )}
        </RdPopover>
    ) : null;

    const betreffSpeichern = () => {
        // Ein geleerter Betreff ist erlaubt: das Backend setzt
        // bei NKG/JG/GAB dann das Kürzel zurück.
        const t = titel.trim();
        if (t !== betreffOhneArt(vorgang.vorgangsart, vorgang.titel)) onPatch({ titel: t });
        else onTitel(betreffOhneArt(vorgang.vorgangsart, vorgang.titel));
    };
    /* Auf dem Handy bricht der Betreff um (ein `input` schnitt ihn ab): ein
       Textfeld, das mit dem Inhalt wächst. Enter speichert statt umzubrechen. */
    const betreffRef = React.useRef<HTMLTextAreaElement | null>(null);
    React.useLayoutEffect(() => {
        const el = betreffRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [titel, mobil]);
    const betreffFeld = (
        <textarea
            ref={betreffRef}
            rows={1}
            className="vg4-betreff vg4m-betreff"
            value={titel}
            disabled={!canEdit}
            aria-label="Betreff des Vorgangs"
            placeholder="Betreff…"
            onChange={(e) => onTitel(e.target.value.replace(/\n/g, " "))}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                }
            }}
            onBlur={betreffSpeichern}
        />
    );

    const erledigtKnopf = (
        <button
            type="button"
            className={`vg3-erledigt${erledigt ? " done" : ""}`}
            disabled={!canEdit}
            aria-pressed={erledigt}
            title={canEdit ? undefined : "Nur Berechtigte können den Status ändern"}
            onClick={() => onPatch({ statusManuell: erledigt ? "Offen" : "Erledigt" })}
        >
            <span className={`vg3-check${erledigt ? " done" : ""}`} aria-hidden>
                <IcoCheck size={11} />
            </span>
            {erledigt ? "erledigt" : "als erledigt markieren"}
        </button>
    );

    const fristen = (
        <div className="vg4-fristen">
            <span className="vg4-frist">
                <span className="vg4-frist-label">TWV</span>
                <DatumChipV4
                    label="Wiedervorlage"
                    value={vorgang.twv}
                    canEdit={canEdit}
                    erledigt={erledigt}
                    onSave={(v) => onPatch(twvPatch(v, vorgang.frist))}
                />
            </span>
            <span className="vg4-frist">
                <span className="vg4-frist-label">Frist</span>
                <DatumChipV4
                    label="Frist"
                    value={vorgang.frist}
                    canEdit={canEdit}
                    erledigt={erledigt}
                    onSave={(v) => onPatch({ frist: v })}
                />
            </span>
        </div>
    );

    const rollen = (
        <div className="vg4-rollen">
            <Rolle gruppe="hauptverantwortlich" label="Hauptverantwortlich" vorgang={vorgang} users={users} canEdit={canEdit} onChange={onPatchBeteiligte} />
            <Rolle gruppe="bearbeiter" label="Bearbeiter" vorgang={vorgang} users={users} canEdit={canEdit} onChange={onPatchBeteiligte} />
            <Rolle gruppe="zuInformierende" label="zu informieren" vorgang={vorgang} users={users} canEdit={canEdit} onChange={onPatchBeteiligte} />
        </div>
    );

    const untertitel = (
        <VorgangUntertitelV4
            wert={beschreibung}
            gespeichert={vorgang.beschreibung ?? ""}
            canEdit={canEdit}
            onAendern={onBeschreibung}
            onSpeichern={(t) => onPatch({ beschreibung: t })}
        />
    );

    /*
     * DER KOPF AUF DEM HANDY (Artifact „Vorgangsdetail mobil", Artboard 1):
     *
     *     [Art]                                   gespeichert ✓   ⋯
     *     Vorgangsbetreff (groß)
     *     Untertitel …
     *     ─────────────
     *     (Kunde) Firma · Ansprechpartner           (☎) (✉)
     *     Details ▾
     *
     * Was am Desktop dauerhaft steht — Fristen, Rollen, K·R·V, das A, der
     * Erledigt-Knopf und die Meta-Zeile —, liegt hinter „Details". Es bleibt
     * alles erreichbar; nur der Platz über den To-dos wird frei.
     */
    if (mobil) {
        const metaText = kopfMeta({
            nummer: vorgang.vorgangNbr,
            id: vorgang.id,
            angelegt: vorgang.createdAt,
            ersteller,
            zuletzt,
        });
        return (
            <>
                <div className="vg4m-kopf-z1">
                    <ArtPille
                        art={vorgang.vorgangsart}
                        canEdit={canEdit}
                        onWahl={(a) => onPatch({ vorgangsart: a })}
                    />
                    {persoenlich && <span className="vg3-badge">persönlich</span>}
                    {vorgang.importiert === true && (
                        <span className="vg3-badge" title="Aus einer alten Kundenakte übernommen">importiert</span>
                    )}
                    <span className="vg4m-kopf-r">
                        {savedFlash && <span className="vg3-saved">gespeichert ✓</span>}
                        {mehrMenue}
                    </span>
                </div>
                {saveError && <div className="vg3-saveerr">{saveError}</div>}
                {betreffFeld}
                {untertitel}
                <div className="vg4m-kontakt">
                    <div className="vg4m-kontakt-wer">
                        <ZuweisungFeld
                            slots={slotsFromDto(vorgang)}
                            canEdit={canEdit}
                            persoenlich={persoenlich}
                            onApply={onAnwendenLinks}
                            kundenSchild={kontaktSchild(vorgang.kunde?.status?.status)}
                        />
                    </div>
                    {kontaktKnoepfe}
                </div>
                <button
                    type="button"
                    className={`vg4m-details-knopf${details ? " open" : ""}`}
                    aria-expanded={details}
                    onClick={() => setDetails((d) => !d)}
                >
                    {details ? "Details ausblenden" : "Details"}
                    <ArtPfeil />
                </button>
                {details && (
                    <div className="vg4m-details">
                        {!hatTodos && (
                            <>
                                <div className="vg4m-details-reihe">
                                    {!persoenlich && (
                                        <BezugsKreise
                                            slots={slotsFromDto(vorgang)}
                                            canEdit={canEdit}
                                            onApply={onAnwendenLinks}
                                            gegenstand="des Vorgangs"
                                        />
                                    )}
                                    <AustauschKnopfV4
                                        vorgangId={vorgang.id}
                                        todoId={null}
                                        austausch={austausch}
                                        users={users}
                                        canEdit={canEdit}
                                        onGeaendert={onAustauschGeaendert}
                                        alsSheet
                                    />
                                    {erledigtKnopf}
                                </div>
                                {fristen}
                                {rollen}
                            </>
                        )}
                        <div className="vg4m-details-meta">
                            <span>{metaText}</span>
                            <AnsprechpartnerKnopf
                                kontaktId={vorgang.kunde?.id ?? null}
                                istFirma={vorgang.kunde?.type?.type === "Firma"}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            {/* ── Zeile 1: wem gehört der Vorgang, und wen ruft man dort an.
                Die Zeile beginnt mit dem KUNDEN, nicht mit dem Wort „Vorgang zu“
                (PO 13.09.2026): über der Seite steht ohnehin fett „Vorgänge“ —
                das Wort ein zweites Mal kostet nur den Platz, an dem der
                Kundenname stehen soll. */}
            <div className="vg4-z1">
                {/* Die Art steht seit dem 13.09.2026 HIER, vor dem Kunden —
                    nicht mehr vor dem Betreff. Sonst wäre sie mit der
                    Betreffzeile verschwunden, sobald es To-dos gibt, und der
                    Vorgang hätte keine änderbare Art mehr. */}
                <ArtPille
                    art={vorgang.vorgangsart}
                    canEdit={canEdit}
                    onWahl={(a) => onPatch({ vorgangsart: a })}
                />

                {/* Der Kunden-Chip trägt in V4 ein Schild (Mockup
                    13.09.2026). Mit To-dos ist diese Zeile der ganze Kopf —
                    ohne Schild stünde dort nur ein Firmenname, der ebenso ein
                    Versicherer sein könnte. Das Schild nennt den primären
                    Kontaktstatus, nicht fest „Kunde" (T-332: ein Interessent
                    stand sonst als Kunde da). Die FO/BO-Verantwortlichkeit und
                    das „ändern" dahinter BLEIBEN (PO 13.09.2026): die
                    Zuweisung ist von hier aus erreichbar, und nur von hier. */}
                <ZuweisungFeld
                    slots={slotsFromDto(vorgang)}
                    canEdit={canEdit}
                    persoenlich={vorgang.persoenlich === true}
                    onApply={onAnwendenLinks}
                    kundenSchild={kontaktSchild(vorgang.kunde?.status?.status)}
                />
                <span className="vg4-z1-r">
                    {savedFlash && <span className="vg3-saved">gespeichert ✓</span>}
                    {saveError && <span className="vg3-saveerr">{saveError}</span>}
                    <AnsprechpartnerKnopf
                        kontaktId={vorgang.kunde?.id ?? null}
                        istFirma={vorgang.kunde?.type?.type === "Firma"}
                    />
                </span>
            </div>

            {/* ── Zeile 2: Betreff und erledigen. Sie steht VOR den Fristen
                (PO 13.09.2026): erst worum es geht, dann bis wann.

                Sie steht seit dem 18.09.2026 IMMER da — auch mit To-dos. Der
                Vorgang hat eine Überschrift, und das erste To-do trägt
                denselben Text, weil es aus ihm entstanden ist. Vom 13. bis
                18.09.2026 wich die Zeile dem To-do aus (`betreffImKopf`).

                Die Reihe RECHTS hängt weiter allein an `hatTodos`: Bezüge,
                das A und der Erledigt-Knopf waren schon vorher daran
                gebunden — mit To-dos leitet sich der Status aus ihnen ab. */}
            <div className="vg4-z3">
                <input
                    className="vg4-betreff"
                    value={titel}
                    disabled={!canEdit}
                    aria-label="Betreff des Vorgangs"
                    placeholder="Betreff…"
                    onChange={(e) => onTitel(e.target.value)}
                    onBlur={() => {
                        // Ein geleerter Betreff ist erlaubt: das Backend setzt
                        // bei NKG/JG/GAB dann das Kürzel zurück.
                        const t = titel.trim();
                        if (t !== betreffOhneArt(vorgang.vorgangsart, vorgang.titel)) onPatch({ titel: t });
                        else onTitel(betreffOhneArt(vorgang.vorgangsart, vorgang.titel));
                    }}
                />

                {/* Die Reihe rechts in der Ordnung der Zeichnung vom
                    13.09.2026: [K R V] [⊕] … [als erledigt markieren] — seit
                    T-302 (PO 29.10.2026) steht an der Stelle des ⊕ das A. */}
                {!hatTodos && (
                    <div className="vg4-z3-r">
                        {persoenlich && <span className="vg3-badge">persönlich</span>}
                        {vorgang.importiert === true && (
                            <span className="vg3-badge" title="Aus einer alten Kundenakte übernommen">importiert</span>
                        )}

                        {/* K · R · V auch am Vorgang — bisher lagen die Bezüge nur
                            in der Kundenzeile darüber (PO 13.09.2026: „Und dann
                            hier noch mal KRV.“ — „Genau, auf jeden Fall.“).
                            ANNAHME: Kundenzeile UND Kreise, nicht statt. Die Zeile
                            nennt den Kunden lesbar, die Kreise tragen R/V und die
                            Mehrfachanzeige als Hochzahl.
                            Beim persönlichen Vorgang gibt es überhaupt keine. */}
                        {!persoenlich && (
                            <BezugsKreise
                                slots={slotsFromDto(vorgang)}
                                canEdit={canEdit}
                                onApply={onAnwendenLinks}
                                gegenstand="des Vorgangs"
                            />
                        )}

                        {/* DAS A STATT DES ⊕ (T-302, PO 29.10.2026: „Das soll
                            den + Button im Vorgang ersetzen"). Was das ⊕ anbot,
                            gibt es weiter: „Risiko/Vertrag zuweisen" an den
                            R- und V-Kreisen links daneben („hinzufügen"),
                            Neue Mail, Mail hinzufügen, Anruf, Dokument, Neues
                            Dokument und Neuer Termin in der Aktionsleiste am
                            Fuß der Karte (`AktionsLeisteV4`) — beide Wirte
                            lasen dieselbe Liste (`aktionsKnoepfe.tsx`). Nur
                            das ⊕ der Vorgangs-Sonderkarte bleibt
                            (`VorgangAlsTodoKarteV4`): Dort gibt es keinen
                            Austausch, der Vorgang hat dann schon To-dos. */}
                        <AustauschKnopfV4
                            vorgangId={vorgang.id}
                            todoId={null}
                            austausch={austausch}
                            users={users}
                            canEdit={canEdit}
                            onGeaendert={onAustauschGeaendert}
                        />

                        {/* Das ⋯ steht zwischen A und dem Erledigen-Knopf —
                            dieselbe Ordnung wie an der To-do-Karte
                            (`TodoKarteV4`, Zeichnung 13.09.2026), damit die
                            Hand dieselbe Stelle findet.

                            KEIN „Vorgang entfernen" hier: Genau daran ist das
                            ⋯ an der Vorgangs-Sonderkarte im September
                            gescheitert („löschte den VORGANG. Ersatzlos
                            raus.", `VorgangAlsTodoKarteV4`). Löschen bleibt,
                            wo es war — in der Kopfzeile der Seite. */}
                        {mehrMenue}

                        {/* Mit To-dos leitet sich der Status aus ihnen ab — der Knopf
                            wäre dann eine zweite, konkurrierende Wahrheit. */}
                        <button
                            type="button"
                            className={`vg3-erledigt${erledigt ? " done" : ""}`}
                            disabled={!canEdit}
                            aria-pressed={erledigt}
                            title={canEdit ? undefined : "Nur Berechtigte können den Status ändern"}
                            onClick={() => onPatch({ statusManuell: erledigt ? "Offen" : "Erledigt" })}
                        >
                            <span className={`vg3-check${erledigt ? " done" : ""}`} aria-hidden>
                                <IcoCheck size={11} />
                            </span>
                            {erledigt ? "erledigt" : "als erledigt markieren"}
                        </button>
                    </div>
                )}
                {/* Mit To-dos steht die Reihe nicht — das ⋯ aber schon
                    (PO 28.09.2026: Zusammenführen und Teilen gelten gerade
                    für gegliederte Vorgänge). */}
                {hatTodos && (mehrMenue || vorgang.importiert === true) && (
                    <div className="vg4-z3-r">
                        {vorgang.importiert === true && (
                            <span className="vg3-badge" title="Aus einer alten Kundenakte übernommen">importiert</span>
                        )}
                        {mehrMenue}
                    </div>
                )}
            </div>

            {/* ── Zeile 3: bis wann, und von wem. */}
            {!hatTodos && (
                <div className="vg4-z2">
                    <div className="vg4-fristen">
                        <span className="vg4-frist">
                            <span className="vg4-frist-label">TWV</span>
                            <DatumChipV4
                                label="Wiedervorlage"
                                value={vorgang.twv}
                                canEdit={canEdit}
                                erledigt={erledigt}
                                onSave={(v) => onPatch(twvPatch(v, vorgang.frist))}
                            />
                        </span>
                        <span className="vg4-frist">
                            <span className="vg4-frist-label">Frist</span>
                            <DatumChipV4
                                label="Frist"
                                value={vorgang.frist}
                                canEdit={canEdit}
                                erledigt={erledigt}
                                onSave={(v) => onPatch({ frist: v })}
                            />
                        </span>
                    </div>

                    <div className="vg4-rollen">
                        <Rolle gruppe="hauptverantwortlich" label="Hauptverantwortlich" vorgang={vorgang} users={users} canEdit={canEdit} onChange={onPatchBeteiligte} />
                        <Rolle gruppe="bearbeiter" label="Bearbeiter" vorgang={vorgang} users={users} canEdit={canEdit} onChange={onPatchBeteiligte} />
                        <Rolle gruppe="zuInformierende" label="zu informieren" vorgang={vorgang} users={users} canEdit={canEdit} onChange={onPatchBeteiligte} />
                    </div>
                </div>
            )}

            {/* ── Die leise Zeile: Nummer, Anlage, letzte Bearbeitung.

                MIT To-dos entfällt sie ganz (PO 13.09.2026, Marker 17): der
                Kopf ist dann nur noch die Kontaktzeile. `kopfMeta()` bleibt
                bestehen — es wird hier nur in einem Zweig nicht mehr gerufen.

                Die drei Ansichts-Pillen „V6 · V5 · V3" standen bis zum
                13.09.2026 dahinter. Sie sind ERSATZLOS gestrichen: der PO hat
                die Ansichten verworfen, und ein Absprung in eine verworfene
                Ansicht ist kein Angebot, sondern eine Falle. Seit dem
                20.09.2026 sind die Ansichten selbst entfernt; ihre Pfade
                leiten hierher weiter (`VorgangAltpfadRedirect`). */}
            {!hatTodos && (
                <div className="vg4-meta">
                    {kopfMeta({
                        nummer: vorgang.vorgangNbr,
                        id: vorgang.id,
                        angelegt: vorgang.createdAt,
                        ersteller,
                        zuletzt,
                    })}
                </div>
            )}

            {/* Untertitel/Beschreibung aus der Anlage — sie sagt, worum es geht;
                die Zusammenfassung darunter sagt, was herauskam.

                Er steht IMMER direkt unter dem Betreff — dorthin gehört er.
                Bis zum 18.09.2026 wanderte er mit dem Betreff an die
                To-do-Karte (`vorgangsZeile`), ein Feld an drei möglichen
                Orten. Jetzt hat er wieder genau einen: „Vorgang mit To-dos hat
                Überschrift und Untertitel." */}
            <VorgangUntertitelV4
                wert={beschreibung}
                gespeichert={vorgang.beschreibung ?? ""}
                canEdit={canEdit}
                onAendern={onBeschreibung}
                onSpeichern={(t) => onPatch({ beschreibung: t })}
            />
        </>
    );
};
